/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { RadioButtonGroup, RadioButton, Callout } from "@octopusdeploy/design-system-components";
import type { EcrOidcFeedAuthentication, OidcFeedAuthentication, SensitiveValue, AcrOidcFeedAuthentication } from "@octopusdeploy/octopus-server-client";
import { isEcrOidc, isAcrOidc } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import styles from "~/areas/infrastructure/components/AccountEdit/style.module.less";
import { AvailableFeedSubjectKeys, generateSampleSubject, OpenIdSubjectMultiSelect, DefaultFeedSubjectKeys } from "~/components/MultiSelect/OpenIdSubjectMultiSelect";
import ExternalLink from "~/components/Navigation/ExternalLink";
import type { SummaryNode } from "~/components/form";
import { ExpandableFormSection, Summary, Text, Sensitive, Note, SensitiveFileUpload } from "~/components/form";
export enum AuthenticationType {
    Anonymous = "Anonymous Access",
    UsernameAndPassword = "Username and Password",
    Token = "Token",
    GoogleCloudJsonKey = "Google Cloud Json Key",
    Oidc = "Oidc",
    AccessKey = "Access Key"
}
interface FeedCredentialsState {
    authType: AuthenticationType;
}
interface FeedCredentialsProps {
    isNew: boolean;
    authenticationType?: AuthenticationType;
    model: {
        username?: string;
        password?: SensitiveValue;
    };
    oidcModel?: OidcFeedAuthentication | EcrOidcFeedAuthentication | AcrOidcFeedAuthentication;
    notes?: JSX.Element;
    supportedAuthenticationTypes: AuthenticationType[];
    onChanged(model: {
        username?: string;
        password?: SensitiveValue;
    }, oidcModel?: OidcFeedAuthentication | EcrOidcFeedAuthentication | AcrOidcFeedAuthentication, authType?: AuthenticationType): void;
    containerKey?: string;
}
const AuthenticationTypeTitles: Record<AuthenticationType, string> = {
    [AuthenticationType.Anonymous]: "Anonymous Access",
    [AuthenticationType.UsernameAndPassword]: "Username and Password",
    [AuthenticationType.Token]: "Token",
    [AuthenticationType.GoogleCloudJsonKey]: "Google Cloud JSON Key",
    [AuthenticationType.Oidc]: "OpenID Connect",
    [AuthenticationType.AccessKey]: "Access Key",
};
const googleAuthUsername: string = "_json_key";
export default class AdvancedFeedCredentialsFormSection extends React.Component<FeedCredentialsProps, FeedCredentialsState> {
    constructor(props: FeedCredentialsProps) {
        super(props);
        let authType = AuthenticationType.Anonymous;
        if (this.props.supportedAuthenticationTypes.some((t) => t === AuthenticationType.AccessKey) && this.props.model.username !== null && this.props.model.username !== undefined) {
            authType = AuthenticationType.AccessKey;
        }
        else if (this.props.oidcModel !== undefined && this.props.oidcModel !== null) {
            authType = AuthenticationType.Oidc;
        }
        else {
            authType = props.supportedAuthenticationTypes.length === 1 ? props.supportedAuthenticationTypes[0] : AuthenticationType.Anonymous;
        }
        this.state = {
            authType: authType,
        };
    }
    componentDidMount() {
        let authType = AuthenticationType.Anonymous;
        if (this.props.authenticationType !== undefined && !this.props.isNew) {
            authType = this.props.authenticationType;
        }
        else {
            authType = this.props.supportedAuthenticationTypes.length === 1 ? this.props.supportedAuthenticationTypes[0] : this.props.isNew ? AuthenticationType.Anonymous : this.getAuthType();
        }
        this.setState({ authType });
    }
    render() {
        const authenticationTypeOptions: JSX.Element[] = [];
        this.props.supportedAuthenticationTypes.forEach((authType) => {
            authenticationTypeOptions.push(<RadioButton value={authType} label={AuthenticationTypeTitles[authType]} key={authType.toString()}/>);
        });
        return (<ExpandableFormSection errorKey="Credentials" title="Credentials" summary={this.renderSummary(this.state.authType)} help={this.props.supportedAuthenticationTypes.length === 1 ? this.props.supportedAuthenticationTypes[0].toString() : "Select an authentication type."} containerKey={this.props.containerKey}>
                {this.props.supportedAuthenticationTypes.length !== 1 && (<RadioButtonGroup value={this.state.authType} onChange={this.handleAuthTypeChange}>
                        {authenticationTypeOptions}
                    </RadioButtonGroup>)}
                {this.renderFields(this.state.authType)}
                {this.props.notes && <Note>{this.props.notes}</Note>}
            </ExpandableFormSection>);
    }
    private handleAuthTypeChange = (authType: AuthenticationType) => {
        this.setState({ authType }, () => {
            switch (this.state.authType) {
                case AuthenticationType.Token:
                    this.props.onChanged({ ...this.props.model, username: undefined });
                    break;
                case AuthenticationType.GoogleCloudJsonKey:
                    this.props.onChanged({ ...this.props.model, username: googleAuthUsername, password: undefined });
                    break;
                case AuthenticationType.Oidc:
                    this.props.onChanged({ ...this.props.model, username: undefined, password: undefined }, this.props.oidcModel, AuthenticationType.Oidc);
                    break;
                case AuthenticationType.AccessKey:
                    this.props.onChanged({ ...this.props.model, username: "", password: undefined }, undefined, AuthenticationType.AccessKey);
                    break;
                default:
                    this.props.onChanged({ username: undefined, password: undefined });
                    break;
            }
        });
    };
    private renderSummary = (authType: AuthenticationType): SummaryNode => {
        switch (authType) {
            case AuthenticationType.UsernameAndPassword:
                return Summary.summary(`Credentials have been entered; username is ${this.props.model.username}`);
            case AuthenticationType.Token:
                return Summary.summary(`A token has been entered`);
            case AuthenticationType.GoogleCloudJsonKey:
                return Summary.summary(`A JSON key file has been uploaded`);
            case AuthenticationType.Oidc:
                return Summary.summary(`Oidc authentication has been selected`);
            case AuthenticationType.AccessKey:
                return Summary.summary(`Access key has been entered; ${this.props.model.username}`);
            default:
                return Summary.placeholder("Add authentication details if the feed requires authentication");
        }
    };
    private renderFields = (authType: AuthenticationType) => {
        const model = this.props.model;
        if (authType === AuthenticationType.Anonymous) {
            return null;
        }
        if (authType === AuthenticationType.UsernameAndPassword) {
            return (<React.Fragment>
                    <Text value={model.username!} onChange={(username) => this.props.onChanged({ ...model, username })} label="Feed Username"/>
                    <Sensitive value={model.password!} onChange={(password) => this.props.onChanged({ ...model, password })} label={"Feed Password"}/>
                </React.Fragment>);
        }
        if (authType === AuthenticationType.Token) {
            return <Sensitive value={model.password!} onChange={(password) => this.props.onChanged({ ...model, password })} label={"Personal Access Token"}/>;
        }
        if (authType === AuthenticationType.GoogleCloudJsonKey) {
            return (<React.Fragment>
                    <SensitiveFileUpload value={model.password!} onChange={(password) => this.props.onChanged({ ...model, password: { ...password, NewValue: atob(password.NewValue ?? "") } })} label="Key File"/>
                    <Note>
                        Refer to the <ExternalLink href="GoogleCloudServiceAccountKey">Google cloud documentation</ExternalLink> for information on generating json key for service accounts.
                    </Note>
                </React.Fragment>);
        }
        if (authType === AuthenticationType.AccessKey) {
            return (<React.Fragment>
                    <Note>
                        The AWS access key to use when authenticating against Amazon Web Services. Refer to the <ExternalLink href="AwsDocsManagingAccessKeys">AWS documentation</ExternalLink> for information on generating access and secret keys.
                    </Note>
                    <Text value={model.username ?? ""} onChange={(username) => this.props.onChanged({ ...model, username })} label="Access Key"/>
                    <Sensitive value={model.password!} onChange={(password) => this.props.onChanged({ ...model, password })} label="Secret Key"/>
                </React.Fragment>);
        }
        if (authType === AuthenticationType.Oidc) {
            return (<React.Fragment>
                    <Callout type={"new-feature"} title="Early Access">
                        The OpenID Connect feature is still in development. We'd love to hear <ExternalLink href={"OpenIDConnectFeedback"}> your feedback</ExternalLink>.
                    </Callout>
                    <br />
                    {isEcrOidc(this.props.oidcModel) && (<>
                            <Text value={this.props.oidcModel.RoleArn ?? ""} label="Role ARN" onChange={(RoleArn) => this.props.onChanged(this.props.model, { ...this.props.oidcModel, RoleArn }, AuthenticationType.Oidc)}/>
                            <Note>The Amazon Resource Name (ARN) of the role that the caller is assuming.</Note>
                        </>)}
                    {isAcrOidc(this.props.oidcModel) && (<>
                            <Text value={this.props.oidcModel.ClientId ?? ""} label="Client ID" onChange={(clientId) => isAcrOidc(this.props.oidcModel) && this.props.onChanged(this.props.model, { ...this.props.oidcModel, ClientId: clientId }, AuthenticationType.Oidc)}/>
                            <Text value={this.props.oidcModel.TenantId ?? ""} label="Tenant ID" onChange={(tenantId) => isAcrOidc(this.props.oidcModel) && this.props.onChanged(this.props.model, { ...this.props.oidcModel, TenantId: tenantId }, AuthenticationType.Oidc)}/>
                        </>)}
                    <OpenIdSubjectMultiSelect onChange={(subjects) => this.props.onChanged(this.props.model, { ...this.props.oidcModel, SubjectKeys: subjects ?? [] })} value={this.props.oidcModel?.SubjectKeys ?? []} subjectKeys={AvailableFeedSubjectKeys}/>
                    <Note>
                        <b>Subject claim format: </b>
                        <span>{generateSampleSubject(this.props.oidcModel?.SubjectKeys ?? [], DefaultFeedSubjectKeys, "", AvailableFeedSubjectKeys)}</span>
                        <br />
                        <span className={styles.secondaryTextColor}>If no keys are specified, the subject claim will default to: {DefaultFeedSubjectKeys.join(", ")}. Any keys not selected will not be included in the subject claim.</span>
                    </Note>
                    <Text value={this.props.oidcModel?.Audience ?? ""} onChange={(audience) => this.props.onChanged(this.props.model, { ...this.props.oidcModel, Audience: audience })} label="Audience"/>
                </React.Fragment>);
        }
        return null;
    };
    private getAuthType() {
        if (this.props.model.username === googleAuthUsername)
            return AuthenticationType.GoogleCloudJsonKey;
        if (!this.props.model.username && this.props.oidcModel !== null)
            return AuthenticationType.Oidc;
        return !this.props.model.username ? (this.props.model.password && this.props.model.password.HasValue ? AuthenticationType.Token : AuthenticationType.Anonymous) : AuthenticationType.UsernameAndPassword;
    }
    static displayName = "AdvancedFeedCredentialsFormSection";
}
